import type { FanUser, Member, DiscographyRelease, Schedule, ChekiPricing, ChekiType, ChekiOrderStatus } from '@tsuyosa/types';

const API_URL = import.meta.env.VITE_API_URL ?? '';

async function apiFetch<T>(path: string, token: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options?.headers,
    },
  });
  const json = (await res.json()) as { success: boolean; data: T; error?: { code: string; message: string } };
  if (!res.ok || !json.success) throw new Error(json.error?.message ?? `API error at ${path}`);
  return json.data;
}

export function getProfile(token: string): Promise<FanUser> {
  return apiFetch<FanUser>('/api/v1/me/profile', token);
}

export interface UpdateProfilePayload {
  display_name?: string;
  bio?: string;
}

export function updateProfile(token: string, payload: UpdateProfilePayload): Promise<FanUser> {
  return apiFetch<FanUser>('/api/v1/me/profile', token, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

async function publicFetch<T>(path: string, params?: Record<string, string>): Promise<T> {
  const q = params ? `?${new URLSearchParams(params).toString()}` : '';
  const res = await fetch(`${API_URL}${path}${q}`);
  const json = (await res.json()) as { success: boolean; data: T; error?: { code: string; message: string } };
  if (!res.ok || !json.success) throw new Error(json.error?.message ?? `API error at ${path}`);
  return json.data;
}

export function getPublicMembers(options?: { activity_status?: string }): Promise<Member[]> {
  return publicFetch<Member[]>('/api/v1/members', options as Record<string, string> | undefined);
}

export function getPublicDiscography(options?: { type?: string }): Promise<DiscographyRelease[]> {
  return publicFetch<DiscographyRelease[]>('/api/v1/discography', options as Record<string, string> | undefined);
}

// ─── Cheki ───────────────────────────────────────────────────────────────────

export function getChekiSchedules(): Promise<Schedule[]> {
  return publicFetch<Schedule[]>('/api/v1/schedules', { cheki_enabled: 'true', status: 'upcoming' });
}

export interface ScheduleChekiData {
  cheki_enabled: boolean;
  pricing: ChekiPricing[];
}

export function getScheduleCheki(scheduleId: string): Promise<ScheduleChekiData> {
  return publicFetch<ScheduleChekiData>(`/api/v1/schedules/${scheduleId}/cheki`);
}

export interface ChekiOrderPayload {
  schedule_id: string;
  buyer_name: string;
  email: string;
  contact?: string;
  type: ChekiType;
  member_id?: string;
  quantity: number;
}

export function submitChekiOrder(payload: ChekiOrderPayload, token?: string | null): Promise<{ id: string }> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return fetch(`${API_URL}/api/v1/cheki/orders`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  }).then(async res => {
    const json = await res.json() as { success: boolean; data: { id: string }; error?: { message: string } };
    if (!res.ok || !json.success) throw new Error(json.error?.message ?? 'Gagal submit order');
    return json.data;
  });
}

// ─── My Orders ───────────────────────────────────────────────────────────────

export interface MyOrder {
  id: string;
  schedule_id: string;
  schedule_title: string;
  schedule_date: string;
  type: ChekiType;
  member_nickname: string | null;
  quantity: number;
  total_price: number;
  status: ChekiOrderStatus;
  created_at: string;
}

export interface MyOrdersResult {
  orders: MyOrder[];
  pagination: { page: number; limit: number; total: number };
}

export function getMyOrders(token: string, params?: { page?: number; status?: ChekiOrderStatus }): Promise<MyOrdersResult> {
  const q: Record<string, string> = {};
  if (params?.page) q['page'] = String(params.page);
  if (params?.status) q['status'] = params.status;
  return apiFetch<MyOrdersResult>(`/api/v1/me/orders${Object.keys(q).length ? `?${new URLSearchParams(q)}` : ''}`, token);
}
